/**
 * Serviço de Salvamento no CorelDRAW
 * 
 * @deprecated Este serviço foi movido para src/lib/corel/actions/save.ts
 * Use: import { SaveService } from '../../../lib/corel/actions/save'
 * 
 * Mantido temporariamente para compatibilidade
 * Origem: linhas 747-1075 do useSaveController.ts
 */

import { SaveService as NewSaveService } from '../../../lib/corel/actions/save';
import type {
  SaveFileOptions as NewSaveFileOptions,
  SaveFileResult as NewSaveFileResult
} from '../../../lib/corel/actions/save.types';
import { sendToTerminal, terminalLog } from '../../../lib/terminalLogger';
import { getActionTimeout } from '../../../lib/corel/timeoutService';
import { validateCorelPayload } from '../utils/payloadValidators';
import { ERROR_NO_SAVE_DATA, ERROR_NO_PATH_RETURNED } from '../constants/saveConstants';

/**
 * Opções para salvar arquivo no Corel
 * @deprecated Use SaveFileOptions de src/lib/corel/actions/save.types
 */
export interface CorelSaveOptions {
  /**
   * Dados de salvamento (workingData)
   */
  workingData: any;
  
  /**
   * Metadata construída via buildMetadataPayload
   */
  metadata: any;
  
  /**
   * Role do usuário atual
   */
  role?: string;
  
  /**
   * Função de notificação
   */
  notify: (notification: {
    type: 'success' | 'error' | 'warning' | 'info';
    title: string;
    message: string;
    sticky?: boolean;
  }) => void;
  
  /**
   * Referência para armazenar o último metadado salvo
   */
  lastSaveMetaRef?: React.MutableRefObject<any>;
  
  /**
   * Referência para armazenar o caminho do arquivo salvo
   */
  savedFilePathRef?: React.MutableRefObject<string>;
}

/**
 * Resultado do salvamento no Corel
 */
export interface CorelSaveResult {
  /**
   * Se o salvamento foi bem-sucedido
   */
  ok: boolean;
  
  /**
   * Caminho do arquivo salvo (se sucesso)
   */
  caminhoSalvo?: string;
  
  /**
   * Mensagem de erro (se falha)
   */
  error?: string;
  
  /**
   * Informações de integridade (se disponível)
   */
  integridade?: {
    objetosVerificados: number;
    metadadosOk: number;
    metadadosPerdidos: number;
  };
}

/**
 * Serviço de salvamento de arquivos no CorelDRAW
 * @deprecated Use SaveService de src/lib/corel/actions/save
 */
export class CorelSaveService {
  /**
   * Salva arquivo no CorelDRAW com validações estritas
   * 
   * @deprecated Use SaveService.saveFile() de src/lib/corel/actions/save
   * Mantido para compatibilidade - delega para novo serviço
   * 
   * @param options - Opções de salvamento
   * @returns Resultado do salvamento
   */
  static async saveFile(options: CorelSaveOptions): Promise<CorelSaveResult> {
    const {
      workingData,
      metadata,
      role,
      notify,
      lastSaveMetaRef,
      savedFilePathRef
    } = options;

    try {
      // Delegar para novo serviço centralizado
      const newOptions: NewSaveFileOptions = {
        workingData,
        metadata,
        role,
        useHmac: true, // ✅ CORREÇÃO: HMAC obrigatório para todas as actions
        timeoutMs: getActionTimeout('salvararquivo'), // timeout do painel admin (fallback: 1h)
        notify,
        onSuccess: (result) => {
          // Atualizar refs (compatibilidade)
          if (lastSaveMetaRef) {
            lastSaveMetaRef.current = result.metadata || {};
          }
          if (savedFilePathRef && result.caminhoSalvo) {
            savedFilePathRef.current = result.caminhoSalvo;
          }
        }
      };

      const result = await NewSaveService.saveFile(newOptions);

      // Converter resultado para formato antigo
      return {
        ok: result.success,
        caminhoSalvo: result.caminhoSalvo,
        error: result.error,
        integridade: result.integridade
      };

    } catch (e: any) {
      sendToTerminal('❌ [SAVE:COREL:LEGACY] Exceção ao salvar arquivo', {
        error: e.message,
        stack: e.stack
      });
      return { ok: false, error: e.message || 'Erro desconhecido ao salvar' };
    }
  }
  /**
   * @deprecated Métodos privados removidos - funcionalidade migrada para SaveService
   * Use SaveService de src/lib/corel/actions/save diretamente
   */
}

